// arabicDigitalTransformation.ts
// Mission 114 - في قلب الحدث - التحول الرقمي
// Plateau TV arabe : transformation digitale dans les entreprises

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
} from './storyLoader';

export interface ArabicDigitalTransformationScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const arabicDTCharacters: Character[] = [
  { id: 'leila',  name: 'ليلى الحسيني',   role: 'مقدمة البرنامج',           color: '#B45309', avatar: 'arabic_tv_leila_woman_30s' },
  { id: 'karim',  name: 'كريم المنصوري',  role: 'مستشار التحول الرقمي',     color: '#1E3A5F', avatar: 'arabic_tv_karim_man_40s'   },
  { id: 'omar',   name: 'عمر البكري',      role: 'خبير استراتيجية الشركات',  color: '#065F46', avatar: 'arabic_tv_omar_man_30s'    },
];

// ─── Scène 1 ──────────────────────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'adt-scene-1',
  title: 'الحلقة الأولى : قصتان من ميدان التحول الرقمي',
  duration: 5,
  content: {
    narrativeText:
      "استوديو «في قلب الحدث» — شاشات تعرض أرقام: التحول الرقمي سوق بقيمة 2.5 تريليون دولار عالمياً.\n\n" +
      "**ليلى :** «اليوم نناقش قضية تشغل كل مسؤول في المنطقة العربية : لماذا تنجح بعض مشاريع التحول الرقمي وتفشل أخرى؟ ضيفنا الخبير شاهد الحالتين عن كثب.»\n\n" +
      "**اللاعب :** «شكراً ليلى. دعوني أبدأ بقصتين حقيقيتين. الأولى : مصرف إقليمي كبير استثمر 50 مليون دولار في التحول الرقمي. بعد ثلاث سنوات : صفر تحول حقيقي. الثانية : شركة متوسطة استثمرت 2 مليون دولار وقلبت نموذجها التجاري كلياً في 18 شهراً.»\n\n" +
      "**كريم :** «ما الفرق الجوهري بين الحالتين؟»\n\n" +
      "**اللاعب :** «المصرف اشترى تكنولوجيا. الشركة الصغيرة غيّرت ثقافتها أولاً. التحول الرقمي 20% تكنولوجيا و80% ناس وعمليات.»\n\n" +
      "**عمر :** «هل يعني ذلك أن التكنولوجيا ليست المشكلة؟»\n\n" +
      "**اللاعب :** «التكنولوجيا محلولة. المشكلة الحقيقية هي مقاومة التغيير، وغياب رعاية تنفيذية (executive sponsorship)، وعدم مشاركة الموظفين في التصميم.»\n\n" +
      "**ليلى :** «كيف يمكن للمؤسسة العربية التقليدية أن تبدأ هذا التحول؟»\n\n" +
      "**اللاعب :** «بثلاث خطوات : أولاً، قيادة ملتزمة بالقمة. ثانياً، نموذج تجريبي صغير يُثبت القيمة بسرعة. ثالثاً، بناء كفاءات داخلية لا الاعتماد على استشاريين إلى الأبد.»\n\n" +
      "**كريم :** «ما مدة التحول الرقمي المتوقعة لشركة متوسطة؟»\n\n" +
      "**اللاعب :** «ثلاث إلى خمس سنوات لتحول عميق، مع نتائج ملموسة في السنة الأولى إذا كانت الاستراتيجية صحيحة.»",
    characters: ['leila', 'karim', 'omar'],
  } as StoryContent,
};

// ─── Card ─────────────────────────────────────────────────────────────────────

const card1: SequenceItem = {
  type: 'card',
  id: 'adt-card-1',
  title: 'ركائز التحول الرقمي',
  duration: 5,
  content: {
    title: 'الركائز الأربع للتحول الرقمي الناجح',
    body:
      "## ركائز التحول الرقمي\n\n" +
      "### الركيزة الأولى : الاستراتيجية (Strategy)\n" +
      "- تحديد «لماذا» التحول قبل «كيف» التحول\n" +
      "- ربط التحول الرقمي بأهداف الأعمال الجوهرية\n" +
      "- بناء خارطة طريق واضحة بأولويات قابلة للقياس\n\n" +
      "### الركيزة الثانية : الإنسان والثقافة (People & Culture)\n" +
      "- بناء ثقافة مؤسسية تتقبّل التجريب والفشل الذكي\n" +
      "- إشراك الموظفين في عملية التصميم (co-design)\n" +
      "- تطوير كفاءات رقمية داخلية\n\n" +
      "### الركيزة الثالثة : العمليات (Processes)\n" +
      "- إعادة تصميم العمليات قبل رقمنتها\n" +
      "- القضاء على الاختناقات البيروقراطية\n" +
      "- تطبيق منهجيات Agile وLean\n\n" +
      "### الركيزة الرابعة : التكنولوجيا (Technology)\n" +
      "- اختيار التقنيات المناسبة للسياق، لا الأحدث\n" +
      "- Cloud First كمبدأ أساسي\n" +
      "- الأمن السيبراني بالتصميم (Security by Design)\n\n" +
      "### نماذج ناجحة عربياً\n" +
      "| الشركة | التحول | النتيجة |\n" +
      "|---|---|---|\n" +
      "| بنك الإمارات دبي الوطني | Digital Banking | 70% من الخدمات رقمية |\n" +
      "| سوق.كوم (Souq/Amazon) | E-Commerce | الاستحواذ بـ580 مليون دولار |\n" +
      "| كريم (Careem) | Super App | 3.1 مليار دولار |\n\n" +
      "### أسباب الفشل الشائعة\n" +
      "- غياب رعاية الإدارة العليا (C-suite sponsorship)\n" +
      "- التحول التكنولوجي دون تغيير العمليات\n" +
      "- إهمال إدارة التغيير (Change Management)\n" +
      "- محدودية الميزانية المخصصة للتدريب",
  } as CardContent,
};

// ─── Chat ─────────────────────────────────────────────────────────────────────

const chat1: SequenceItem = {
  type: 'chat',
  id: 'adt-chat-1',
  title: 'نقاش : استراتيجية التحول الرقمي',
  duration: 6,
  content: {
    tutorCharacterId: 'omar',
    messages: [
      {
        id: 'adt-chat-q1',
        sender: 'character',
        characterId: 'omar',
        text: 'شركة تقليدية تريد البدء في التحول الرقمي. ما أول خطوة يجب أن تتخذها؟',
        type: 'single-choice',
        options: [
          'شراء أحدث برامج ERP في السوق فوراً',
          'تعيين فريق IT جديد وتوسيعه',
          'إجراء تقييم شامل للنضج الرقمي (Digital Maturity Assessment)',
          'الانتقال الفوري إلى السحابة',
        ],
        correctAnswer: 'إجراء تقييم شامل للنضج الرقمي (Digital Maturity Assessment)',
        explanation: 'تقييم النضج الرقمي هو نقطة البداية الصحيحة. يكشف عن الفجوات الحالية، يحدد الأولويات، ويوفر أساساً لقياس التقدم. بدونه، الاستثمار في التكنولوجيا يصبح حلولاً بحثاً عن مشكلات.',
      },
      {
        id: 'adt-chat-q2',
        sender: 'character',
        characterId: 'omar',
        text: 'ما المعنى الدقيق لـ Cloud First كاستراتيجية؟',
        type: 'single-choice',
        options: [
          'نقل جميع البيانات إلى السحابة في يوم واحد',
          'التفضيل المبدئي للحلول السحابية عند اختيار التقنيات الجديدة',
          'إغلاق جميع الخوادم الداخلية فوراً',
          'استخدام السحابة فقط للنسخ الاحتياطي',
        ],
        correctAnswer: 'التفضيل المبدئي للحلول السحابية عند اختيار التقنيات الجديدة',
        explanation: 'Cloud First تعني أنه عند اتخاذ قرار تقني جديد، يكون الخيار السحابي هو الافتراضي ما لم تكن هناك أسباب قوية لغير ذلك (مثل متطلبات أمنية أو تنظيمية محددة). إنها استراتيجية اتجاه، لا إلزام كلي.',
      },
      {
        id: 'adt-chat-q3',
        sender: 'character',
        characterId: 'omar',
        text: 'لماذا تُعدّ إدارة التغيير (Change Management) العامل الأكثر إهمالاً في مشاريع التحول؟',
        type: 'single-choice',
        options: [
          'لأنها باهظة التكلفة جداً',
          'لأن قادة التقنية يركزون على الأنظمة لا على الناس، والعائد على الاستثمار فيها غير ملموس فوراً',
          'لأنها تنتمي لإدارة الموارد البشرية وليس IT',
          'لأنها مفهوم قديم لا يناسب العصر الرقمي',
        ],
        correctAnswer: 'لأن قادة التقنية يركزون على الأنظمة لا على الناس، والعائد على الاستثمار فيها غير ملموس فوراً',
        explanation: 'دراسة McKinsey تؤكد أن 70% من برامج التحول تفشل بسبب مقاومة بشرية لا فشل تقني. قادة التقنية يميلون للتركيز على ما يمكن قياسه (uptime، performance) بدلاً من العوامل الإنسانية الأصعب في القياس.',
      },
    ],
  } as ChatContent,
};

// ─── Quiz ─────────────────────────────────────────────────────────────────────

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'adt-quiz-1',
  title: 'اختبار : التحول الرقمي في المؤسسات',
  duration: 5,
  content: {
    questions: [
      {
        id: 'adt-q1-1',
        question: 'وفق الركائز الأربع، ما النسبة التقريبية لعامل «الإنسان والثقافة» في نجاح التحول الرقمي؟',
        type: 'single-choice',
        options: ['20%', '40%', '60-80%', '100%'],
        correctAnswer: '60-80%',
        explanation: 'معظم الدراسات والممارسين يتفقون أن 60 إلى 80% من نجاح (أو فشل) التحول الرقمي يعود للعوامل البشرية والثقافية، وليس للتكنولوجيا.',
      },
      {
        id: 'adt-q1-2',
        question: 'ما الفرق بين «رقمنة العمليات» (Digitization) و«التحول الرقمي» (Digital Transformation)؟',
        type: 'single-choice',
        options: [
          'المصطلحان يعنيان الشيء ذاته',
          'الرقمنة هي تحويل المحتوى التناظري إلى رقمي، بينما التحول الرقمي يُعيد تصميم النموذج التجاري بأكمله',
          'التحول الرقمي أسرع وأرخص من الرقمنة',
          'الرقمنة للشركات الكبيرة والتحول للشركات الصغيرة',
        ],
        correctAnswer: 'الرقمنة هي تحويل المحتوى التناظري إلى رقمي، بينما التحول الرقمي يُعيد تصميم النموذج التجاري بأكمله',
        explanation: 'Digitization = تحويل الورق إلى PDF مثلاً. Digitalization = تحسين العمليات بالتقنية. Digital Transformation = إعادة بناء الهوية التجارية والعمليات والتجربة بشكل جذري.',
      },
      {
        id: 'adt-q1-3',
        question: 'ما مؤشر الأداء الأنسب لقياس نجاح التحول الرقمي في المرحلة الأولى؟',
        type: 'single-choice',
        options: [
          'عدد التطبيقات المشتراة',
          'نسبة التوفير في تكاليف IT',
          'مستوى اعتماد الموظفين على الأنظمة الجديدة (User Adoption Rate)',
          'سرعة الاتصال بالإنترنت في المكاتب',
        ],
        correctAnswer: 'مستوى اعتماد الموظفين على الأنظمة الجديدة (User Adoption Rate)',
        explanation: 'User Adoption Rate هو المؤشر الأهم في المرحلة الأولى لأنه يعكس قبول الناس للتغيير. شراء تكنولوجيا لا يستخدمها أحد هو مضيعة كاملة للاستثمار.',
      },
    ],
  } as QuizContent,
};

// ─── Chapitre ─────────────────────────────────────────────────────────────────

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'التحول الرقمي في المؤسسات',
  subtitle: 'Arabic — استراتيجية التحول الرقمي في السياق العربي',
  learningObjectives: [
    'فهم الركائز الأربع للتحول الرقمي الناجح',
    'التمييز بين الرقمنة والتحول الرقمي الكامل',
    'تحليل أسباب نجاح وفشل مشاريع التحول',
    'بناء خطاب مهني عربي حول استراتيجية التحول',
  ],
  sequence: [scene1, card1, chat1, quiz1],
};

export const arabicDigitalTransformationScenario: ArabicDigitalTransformationScenario = {
  id: 'arabic-digital-transformation',
  title: 'في قلب الحدث - التحول الرقمي',
  subtitle: 'Transformation digitale dans les entreprises',
  certification: 'Arabic',
  characters: arabicDTCharacters,
  chapters: [chapter1],
};

export default arabicDigitalTransformationScenario;
